import { format } from 'date-fns';
//
import { MenuItem, TableCell, TableRow } from '@mui/material';
import { TableMoreMenu, TableRowLink } from '@/components/table';
import { Iconify } from '@/components';
import { ClientPriceDialog } from '..';
// hooks + utils
import useToggle from '@/hooks/useToggle';
import { use3DotMenu } from '@/hooks/use3DotMenu';
import { fCurrency } from '@/utils/formatNumber';
import { servicesManagementPath } from '@/features/services-management';
// types
import { UserCustomPrice } from '@/features/users/shared/types';

type ClientPricesRowProps = {
  row: UserCustomPrice;
  canUpdate: boolean;
};

export default function ClientPricesTableRow({
  row,
  canUpdate,
}: ClientPricesRowProps) {
  const {
    id,
    cities,
    service_variant,
    price,
    discount_percentage,
    start_date,
    end_date,
    locked,
  } = row;

  const isPriceExpired = new Date(end_date) < new Date() || locked;
  const isEditAllowed = canUpdate && !locked && !isPriceExpired;

  // ============
  //     EDIT
  // ============
  const {
    toggle: isCustomPriceDialogOpen,
    onOpen: openCustomPriceDialog,
    onClose: closeCustomPriceDialog,
  } = useToggle();

  const { openMenu, handleOpenMenu, handleCloseMenu } = use3DotMenu();

  const handleEditRow = () => {
    openCustomPriceDialog();
    handleCloseMenu();
  };

  return (
    <>
      <TableRow
        hover
        sx={{
          color: (theme) =>
            isPriceExpired ? theme.palette.grey[400] : 'inherit',
        }}
      >
        <TableCell width={10} />

        <TableCell
          align="left"
          sx={{
            color: 'inherit',
            paddingLeft: isPriceExpired ? '1.5rem' : 'none',
          }}
        >
          {isEditAllowed ? (
            <TableRowLink
              linkText={cities ? cities.name : 'No city'}
              linkTo="#"
              onClick={handleEditRow}
            />
          ) : (
            <>{cities ? cities.name : 'No city'}</>
          )}
        </TableCell>

        <TableCell align="left" sx={{ color: 'inherit' }}>
          {service_variant.service ? (
            <TableRowLink
              linkText={service_variant.service.name}
              linkTo={servicesManagementPath('editService', {
                serviceId: String(service_variant.service.id),
              })}
              sx={{ color: 'inherit' }}
            />
          ) : (
            'N/A'
          )}
        </TableCell>

        <TableCell align="left" sx={{ color: 'inherit' }}>
          {service_variant ? (
            <TableRowLink
              linkText={service_variant.name}
              linkTo={servicesManagementPath('editVariant', {
                variantId: String(service_variant.id),
                tabSid: 'details',
              })}
              sx={{ color: 'inherit' }}
            />
          ) : (
            'N/A'
          )}
        </TableCell>

        <TableCell align="left" sx={{ color: 'inherit' }}>
          {fCurrency(price)}
        </TableCell>

        <TableCell
          align="left"
          sx={{ color: 'inherit' }}
        >{`${discount_percentage}%`}</TableCell>

        <TableCell align="left" sx={{ color: 'inherit' }}>
          {start_date
            ? format(new Date(start_date as string), 'EEE, dd MMM yyyy')
            : 'N/A'}
        </TableCell>

        <TableCell align="left" sx={{ color: 'inherit' }}>
          {end_date
            ? format(new Date(end_date as string), 'EEE, dd MMM yyyy')
            : 'N/A'}
        </TableCell>

        <TableCell align="left" sx={{ color: 'inherit' }}>
          {
            <Iconify
              icon={
                locked ? 'material-symbols:lock' : 'material-symbols:lock-open'
              }
              color={locked ? 'error.main' : 'success.main'}
            />
          }
        </TableCell>

        <TableCell align="right">
          {isEditAllowed && (
            <TableMoreMenu
              open={openMenu}
              onOpen={handleOpenMenu}
              onClose={handleCloseMenu}
              arrow="top-right"
              actions={
                <MenuItem onClick={handleEditRow}>
                  <Iconify icon="eva:edit-outline" />
                  Edit
                </MenuItem>
              }
            />
          )}
        </TableCell>
      </TableRow>
      <ClientPriceDialog
        isCustomPriceDialogOpen={isCustomPriceDialogOpen}
        closeCustomPriceDialog={closeCustomPriceDialog}
        currentPriceId={String(id)}
      />
    </>
  );
}
